import { cn } from '@/lib/utils'
import { Pill } from '@/features/dossier/components/primitives'

/**
 * War-room primitives.
 *
 * The two that matter carry rules rather than styling: a threat score that has not been
 * measured renders as "not scored" and never as 0, and an engagement metric the platform did
 * not publish renders as "—" and never as zero. Both distinctions decide whether somebody
 * stands down, so neither may be flattened for tidiness.
 */

/**
 * 1–10, coloured by band. Null is "nobody has scored it", which is not the same as harmless.
 *
 * <p>It used to read "not read", which was the domain's own word — an assessment is a READING
 * throughout this feature — and it collided badly with the everyday meaning. An operator who
 * had just opened the thread saw it still marked "not read" and reasonably concluded the screen
 * was wrong. The column is about whether a THREAT SCORE has been recorded, by the council or by
 * a person, and opening a thread records nothing.</p>
 */
export function ThreatScore({ score, threshold }: {
  score: number | null | undefined
  threshold?: number
}) {
  if (score == null) {
    return (
      <span className="text-[11px] text-muted-foreground"
            title="No threat score recorded yet — opening a thread does not score it. Use “Ask the council to read the thread”, or score it yourself.">
        not scored
      </span>
    )
  }
  const tone = score >= (threshold ?? 7)
    ? 'border-rose-500/30 bg-rose-500/10 text-rose-700 dark:text-rose-400'
    : score >= 4
    ? 'border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-400'
    : 'border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400'
  return <Pill className={tone}>{score}/10</Pill>
}

export function SentimentChip({ code }: { code: string | null | undefined }) {
  if (!code) return null
  const tone = code === 'HOSTILE'
    ? 'border-rose-500/30 bg-rose-500/10 text-rose-700 dark:text-rose-400'
    : code === 'NEGATIVE'
    ? 'border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-400'
    : code === 'SUPPORTIVE'
    ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400'
    : 'border-border'
  return <Pill className={tone}>{code.toLowerCase()}</Pill>
}

/** A metric the platform did not publish. "—", never 0. */
export function Metric({ label, value }: { label: string; value: number | null | undefined }) {
  return (
    <span className="text-[10px] text-muted-foreground">
      {value == null ? '—' : value.toLocaleString()} {label}
    </span>
  )
}

/** Engagements per minute. The feed's ranking, so it is on every row. */
export function Velocity({ value }: { value: number | null | undefined }) {
  if (value == null) {
    return <span className="text-[10px] text-muted-foreground">not measured</span>
  }
  const hot = value >= 5
  return (
    <span className={cn('text-[10px] tabular-nums',
      hot ? 'font-medium text-rose-600' : 'text-muted-foreground')}>
      {value.toFixed(1)}/min
    </span>
  )
}

/**
 * Thread status and deployment status share this chip on purpose: an operator scanning the
 * ledger and an operator scanning the feed are asking the same question — is this settled, is
 * it loud, or did it go wrong — and two colour languages for one question is one too many.
 */
export function StatusChip({ code }: { code: string }) {
  const tone =
    code === 'DISMISSED' || code === 'CANCELLED' ? 'border-border text-muted-foreground'
    : code === 'ESCALATED' || code === 'FAILED'
      ? 'border-rose-500/30 bg-rose-500/10 text-rose-700 dark:text-rose-400'
    : code === 'ENGAGED' || code === 'POSTED'
      ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400'
    : code === 'QUEUED' || code === 'SENDING'
      ? 'border-sky-500/30 bg-sky-500/10 text-sky-700 dark:text-sky-400'
    : 'border-border'
  return <Pill className={tone}>{code.toLowerCase().replace(/_/g, ' ')}</Pill>
}

/** Platform, spelled out. MANUAL is not a lesser thread and should not read as one. */
/**
 * Display names for the platform codes the backend registers.
 *
 * An unlisted code falls through to itself rather than to "unknown". A driver added on the
 * server should show up here immediately as a readable-enough code, not disappear behind a
 * placeholder — the chip's job is to say which platform this is, and the raw code does that.
 */
const PLATFORM_LABELS: Record<string, string> = {
  MANUAL: 'entered by hand',
  X: 'X',
  FACEBOOK: 'Facebook',
  INSTAGRAM: 'Instagram',
  YOUTUBE: 'YouTube',
  TIKTOK: 'TikTok',
  TELEGRAM: 'Telegram',
  SNAPCHAT: 'Snapchat',
}

export function PlatformChip({ code }: { code: string }) {
  return <Pill className="border-border">{PLATFORM_LABELS[code] ?? code}</Pill>
}
